<template>
  <div
    :class="cartOpen ? 'gap-[62px]' : ''"
    class="flex flex-row w-full gap-[8px]"
  >
    <img
      v-if="
        !product.specialProduct && !product?.item_sale?.product?.billing_product
      "
      :class="cartOpen ? 'w-[117px] h-[106px]' : ''"
      :src="`https://images.mishnatyosef.org/images/items/${
        product?.item_sale
          ? product?.item_sale?.product?.featured_image
          : product?.featured_image
          ? product?.featured_image
          : product?.product?.featured_image
      }`"
      alt="item"
      class="w-[80px] h-[80px] flex-none rounded-[5px] object-cover"
    />
    <img
      v-else
      :class="cartOpen ? 'w-[117px] h-[106px]' : ''"
      :src="`https://images.mishnatyosef.org/images/items/${product.item_sale.product.featured_image}`"
      alt="item"
      class="w-[80px] h-[80px] flex-none rounded-[5px] object-cover"
    />
    <div
      :class="cartOpen ? '!flex-row items-center justify-between' : ''"
      class="flex flex-col flex-1 justify-between"
    >
      <div class="flex flex-col">
        <!-- Name and tooltip. -->
        <div
          :class="cartOpen ? 'items-center w-fit pl-[0]' : ''"
          class="flex flex-row justify-between w-full relative pl-[24px]"
          @mouseleave="hideTooltip"
          @mouseover="checkTruncation"
        >
          <div
            class="relative group h-fit"
            @mouseleave="isTooltipVisible = false"
            @mouseover="checkTruncation"
          >
            <tooltip-small
              :class="[
                isTooltipVisible ? 'opacity-100 z-40' : 'opacity-0 z-[-1]',
                !isFirstItem(product) ? 'left-[0] bottom-[35px]' : '',
                isFirstItem(product) ? 'left-[0] top-[35px]' : '',
              ]"
              :positionBottom="!isFirstItem(product)"
              :positionTop="isFirstItem(product)"
              :tooltipText="`${
                product.full_name ||
                product?.product?.full_name ||
                product?.item_sale?.product?.full_name
              }`"
              class="tooltip absolute pointer-events-none"
            />
            <p
              ref="textElement"
              :class="cartOpen ? 'max-w-[186px] w-[186px] text-base' : ''"
              class="text-primary-700 text-sm font-bold truncate max-w-[125px] select-none"
            >
              {{
                product.full_name ||
                product?.product?.full_name ||
                product?.item_sale?.product?.full_name
              }}
            </p>
          </div>
          <button
            v-if="
              !cartOpen &&
              !product.specialProduct &&
              !product?.item_sale?.product?.billing_product
            "
            class="w-[24px] h-[24px] flex justify-center items-center absolute left-0"
            :disabled="processing"
            @click="handleRemoveFromCart(product)"
          >
            <span
              :class="[processing ? 'text-grays-500' : 'text-grays-800']"
              class="material-symbols-rounded text-base"
              >delete</span
            >
          </button>
        </div>
        <!-- Small price and price per kg -->

        <p
          v-if="
            !product.specialProduct &&
            !product?.item_sale?.product?.billing_product
          "
          class="text-grays-900 text-sm font-medium"
        >
          {{ product.price1 }} ₪
          <span
            v-if="product.units && product.units_type"
            class="text-grays-700 font-regular"
            >{{ computedPriceForweight }}</span
          >
          <span v-if="!cartOpen" class="text-grays-700 font-regular">{{
            product.attribute
          }}</span>
        </p>
      </div>
      <div v-if="cartOpen" class="text-grays-700 text-base">
        {{ `${product?.attribute_name || ""} ${product?.attribute || ""}` }}
      </div>
      <p
        v-if="cartOpen"
        :class="cartOpen ? 'w-[80px]' : ''"
        class="text-primary-900 text-base font-bold text-center"
      >
        {{ (product.price1 * product.amount).toFixed(2) }} ₪
      </p>
      <!-- Amount input. -->
      <div
        :class="cartOpen ? 'w-[195px] gap-[8px]' : ''"
        class="flex flex-row items-center !justify-between gap-[4px]"
      >
        <input-amount
          v-if="
            !product.specialProduct &&
            !product?.item_sale?.product?.billing_product
          "
          v-model="amount"
          :max-amount="computedMaxAmount"
          :out-of-stock="product.available !== null && product.available === 0"
          :versionMini="true"
          @valueChanged="handleValueChanged"
        />
        <div v-else></div>
        <button-text
          v-if="!buttonDisabled"
          :small="true"
          class="!text-sm !px-[0]"
          @click="updateAmount"
          :disabled="processing"
        >
          לעדכן כמות מוצר
        </button-text>
        <p
          v-if="!cartOpen && buttonDisabled"
          class="text-primary-900 text-base font-bold"
        >
          {{ (product.price1 * (amount || 1)).toFixed(2) }} ₪
        </p>
      </div>
      <button
        v-if="
          cartOpen &&
          !product.specialProduct &&
          !product?.item_sale?.product?.billing_product
        "
        class="w-[24px] h-[24px] flex justify-center items-center"
      >
        <span
          :class="cartOpen ? 'text-lg' : ''"
          class="material-symbols-rounded text-base text-grays-800"
          @click="handleRemoveFromCart(product)"
          >delete</span
        >
      </button>
    </div>
  </div>
</template>

<script>
import { reportRemoveFailure } from "@/helpers/removeItemFailure";
import { mapActions, mapMutations } from "vuex";
import axiosInstance from "../../helpers/axios.js";
import { isSoldOut } from "@/helpers/stock";
import TooltipSmall from "@/components/Global/TooltipSmall.vue";
import InputAmount from "@/components/Global/InputAmount.vue";
import ButtonText from "@/components/Global/ButtonText.vue";
import { mapGetters } from "vuex";

export default {
  name: "cart-product",
  components: { ButtonText, InputAmount, TooltipSmall },
  props: {
    cartOpen: {
      type: Boolean,
      required: true,
    },
    cartProducts: {
      type: Array,
      required: true,
    },
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      amount: this.product.amount,
      originalAmount: this.product.amount,
      buttonDisabled: true,
      isTooltipVisible: false,
      processing: false,
    };
  },
  methods: {
    ...mapMutations({
      removeFromCart: "cart/removeFromCart",
      removeAllFromCart: "cart/removeAllFromCart",
      removeBillingProducts: "cart/removeBillingProducts",
    }),
    ...mapActions({
      showToast: "orderToast/showToast",
    }),
    checkTruncation() {
      const textElement = this.$refs.textElement;
      this.isTooltipVisible = textElement.scrollWidth > textElement.clientWidth;
    },
    showTooltip() {
      this.isTooltipVisible = true;
    },
    hideTooltip() {
      this.isTooltipVisible = false;
    },
    isFirstItem(product) {
      return product.id === this.cartProducts[0].id;
    },
    async handleRemoveFromCart(product) {
      this.processing = true;
      try {
        const { data } = product.organization_item
          ? await axiosInstance.delete(
              `/orders/${this.$route.params.orderID}/organization-products/${product.itemID}`
            )
          : await axiosInstance.post(
              `/remove_item/${this.$route.params.orderID}/${product.item_salesID}`,
              product
            );
        if (data.remove_billing_product) {
          this.removeBillingProducts();
        }
        this.removeFromCart(product);
        await this.showToast({
          message:
            product?.full_name ||
            product?.product?.full_name ||
            product?.item_sale?.product?.full_name,
          type: "danger",
        });
        this.processing = false;
      } catch (e) {
        this.processing = false;
        reportRemoveFailure(e, this.showToast);
      }
    },
    handleValueChanged() {
      this.buttonDisabled = this.originalAmount === this.amount;
    },
    async updateAmount() {
      this.processing = true;
      const name =
        this.product.full_name ||
        this.product?.product?.full_name ||
        this.product?.item_sale?.product?.full_name;
      try {
        if (this.product.organization_item) {
          await axiosInstance.put(
            `/orders/${this.$route.params.orderID}/organization-products/${this.product.itemID}`,
            { amount: this.amount }
          );
        } else {
          await axiosInstance.post(`/update_item/${this.$route.params.orderID}`, {
            ...this.product,
            amount: this.amount,
          });
        }
        this.product.amount = this.amount;
        this.showToast({
          message: name,
          type: "update",
          amount: this.amount,
        });
        this.processing = false;
        this.buttonDisabled = true;
      } catch (e) {
        this.amount = this.product.amount;
        this.showToast({
          message: e.response.data.message,
          type: "error",
        });
        if (e.response.data.message === "המכירה סגורה להזמנות.") {
          setTimeout(() => {
            this.$router.push("/");
          }, 3000);
        }

        this.processing = false;
        this.buttonDisabled = true;
        console.error(e);
      }
    },
    ...mapActions({
      showToast: "orderToast/showToast",
    }),
  },
  mounted() {},
  watch: {
    product: {
      deep: true,
      handler(newValue) {
        if (this.originalAmount !== newValue.amount) {
          this.originalAmount = newValue.amount;
          this.amount = newValue.amount;
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      noLimit: "user/getNoLimit",
    }),
    computedPriceForweight() {
      const units = this.product.units > 0 ? this.product.units + " " : "";
      const unit_type = this.product?.item_unit?.name || "";
      let price = "";
      if (Number(this.product.units) !== 0) {
        if (this.product.units_type == 10 && this.product.units < 1) {
          price =
            " (₪" +
            parseFloat(this.product.price / units / 10).toFixed(2) +
            " ל-100 גרם)";
        } else if (this.product.units_type == 16 && this.product.units < 1) {
          price =
            " (₪" +
            parseFloat(this.product.price / units / 10).toFixed(2) +
            " ל-100 מ״ל)";
        } else {
          price =
            " (₪" +
            parseFloat(this.product.price / units).toFixed(2) +
            " ל" +
            unit_type +
            ")";
        }
      }
      return price;
    },
    // A sold-out line can only go down, so its cap is what the order
    // already holds (see Product.vue).
    computedMaxAmount() {
      if (isSoldOut(this.product)) {
        return this.product.amount;
      } else if (this.noLimit !== 0) {
        return 99999;
      } else {
        return this.product.max_per_man;
      }
    },
  },
};
</script>

<style scoped>
/* Add your styles here */
</style>
